import { copyText, Modal, useToast } from '@pa/ui'
import { Copy } from 'lucide-react'
import { useId } from 'react'

export interface TempPasswordDialogProps {
  /** The person's name ("Share these sign-in details with {name}"). */
  name: string
  email: string
  /** The 14-character temporary password: in this component's props only, never logged or stored. */
  password: string
  /** Done: the only way to close it (Escape and the backdrop are disabled). */
  onDone: () => void
}

/**
 * C-13b Temporary password (UserFlows C-13b; Frontend §6.5), shared by C-13a, Reset password, S-03 and S-05. After
 * Done the password can't be shown again: no API returns it.
 */
export function TempPasswordDialog({ name, email, password, onDone }: TempPasswordDialogProps) {
  const toast = useToast()
  const id = useId()
  const copy = async (text: string) => {
    const ok = await copyText(text)
    toast(ok ? 'Copied' : "Couldn't copy. Select the text and copy it.", ok ? 'success' : 'error')
  }
  return (
    <Modal onClose={onDone} labelledBy={`${id}-title`} describedBy={`${id}-intro`} dismissible={false}>
      <div className="modal-head">
        <div>
          <h2 id={`${id}-title`}>Temporary password</h2>
          <p className="muted" id={`${id}-intro`}>
            Share these sign-in details with {name}. The password is shown only once.
          </p>
        </div>
      </div>
      <div className="modal-body form-grid">
        <div className="field">
          <span className="label" id={`${id}-email-label`}>
            Email
          </span>
          <div className="temp-secret">
            <span className="input static" aria-labelledby={`${id}-email-label`}>
              {email}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              aria-label="Copy email"
              onClick={() => void copy(email)}
            >
              <Copy aria-hidden /> Copy
            </button>
          </div>
        </div>
        <div className="field">
          <span className="label" id={`${id}-password-label`}>
            Temporary password
          </span>
          <div className="temp-secret">
            <span className="input static mono" aria-labelledby={`${id}-password-label`}>
              {password}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              aria-label="Copy temporary password"
              onClick={() => void copy(password)}
            >
              <Copy aria-hidden /> Copy
            </button>
          </div>
        </div>
        <p className="muted">They&apos;ll be asked to choose their own password when they first sign in.</p>
      </div>
      <div className="modal-foot">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => void copy(`Email: ${email}\nTemporary password: ${password}`)}
        >
          Copy both
        </button>
        <button type="button" className="btn btn-primary" onClick={onDone}>
          Done
        </button>
      </div>
    </Modal>
  )
}
